import { createContext, useContext } from 'react';
import type { HomeStats } from './api/preview';

/**
 * Whether this render is the signed-out free preview, and the Home rails' data
 * if so.
 *
 * The rails (Spotlight, Most Starred, Upcoming) each subscribe to a stats doc
 * that firestore.rules closes to signed-out readers. Rather than thread a flag
 * through Home's prop surface into three separate components, the flag and the
 * public copy of that data ride this context, which GuestProvider supplies.
 *
 * Empty (guest: false) everywhere else, so signed-in rendering is untouched.
 */
export interface GuestValue {
  guest: boolean;
  stats: HomeStats | null;
}

export const GuestContext = createContext<GuestValue>({ guest: false, stats: null });

export function useGuest() {
  return useContext(GuestContext);
}

/** The public copy of one stats doc, or null when signed in / not loaded. */
export function useGuestStat<T>(key: keyof HomeStats): T | null {
  const { guest, stats } = useGuest();
  if (!guest) return null;
  return (stats?.[key] as T | undefined) ?? null;
}

const WALL_EVENT = 'gnosis-need-account';

/**
 * Ask for the sign-in wall from anywhere in the guest preview, without
 * threading a callback down through Home's prop surface. Same shape as
 * showToast in utils/toast.ts; AppShell listens and raises the wall.
 *
 * Used by controls that exist in the shell but need an account behind them —
 * the Spotlight "Promote" button being the first.
 */
export type WallReason = 'plays' | 'locked';

export function requestAccount(reason: WallReason = 'locked'): void {
  window.dispatchEvent(new CustomEvent<WallReason>(WALL_EVENT, { detail: reason }));
}

export function onRequestAccount(handler: (reason: WallReason) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<WallReason>).detail ?? 'locked');
  window.addEventListener(WALL_EVENT, listener);
  return () => window.removeEventListener(WALL_EVENT, listener);
}
